import React from "react";
import "./BattleReportSection.css";

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatUnits = (value) => Math.max(0, Math.floor(asNumber(value, 0))).toLocaleString();

const formatOptionalUnits = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return Math.max(0, Math.floor(n)).toLocaleString();
};

const formatWounded = (value) => {
  if (value === null || value === undefined) return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return Math.max(0, Math.floor(n)).toLocaleString();
};

const formatTierLabel = (row = {}) => {
  const explicit = String(row?.tierLevelLabel || row?.tierLabel || row?.levelLabel || "").trim();
  if (explicit) return explicit;
  const tier = Number(row?.troopTier ?? row?.tier);
  if (Number.isFinite(tier) && tier > 0) return `T${Math.floor(tier)}`;
  const gameLevel = Number(row?.troopGameLevel ?? row?.gameLevel);
  if (Number.isFinite(gameLevel) && gameLevel >= 0) return `T${Math.floor(gameLevel) + 1}`;
  return "-";
};

const formatDecimal = (value) => {
  const numeric = asNumber(value, 0);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const formatBonusValue = (metric = {}) => {
  const format = String(metric?.format || "percent");
  const value = asNumber(metric?.value, 0);
  const percent = asNumber(metric?.percent, 0);
  if (format === "raw_plus_percent") {
    return `${formatDecimal(value)} (${formatDecimal(percent)}%)`;
  }
  if (format === "raw") {
    return formatDecimal(value);
  }
  return `${formatDecimal(value)}%`;
};

const normalizeSourceAttribution = (value) => {
  if (!value || typeof value !== "object") {
    return { available: false, entryCount: 0, sources: [] };
  }
  const sources = Array.isArray(value?.sources) ? value.sources : [];
  const entryCount = Math.max(0, Math.floor(asNumber(value?.entryCount, 0)));
  const available = Boolean(value?.available) || entryCount > 0 || sources.length > 0;
  return { available, entryCount: Math.max(entryCount, sources.length), sources };
};

const SOURCE_TYPE_LABELS = Object.freeze({
  basic_attack: "Basic Attack",
  active_skill: "Active Skill",
  update_skill: "Breakthrough Skill",
  armor_skill: "Armor Skill",
  misc_spell: "Battle Spell",
  titan_spell: "Titan Effect",
  warplane_spell: "Warplane Effect",
  counter_reflect: "Counter / Reflect",
});

const SOURCE_TYPE_GROUPS = Object.freeze({
  titan_spell: "titan",
  warplane_spell: "warplane",
  active_skill: "skill",
  update_skill: "skill",
  armor_skill: "skill",
  misc_spell: "spell",
  counter_reflect: "other",
  basic_attack: "basic",
});

const KNOWN_SOURCE_LABELS = Object.freeze({
  100859982: "Air Support",
  100859984: "EMP Bomb",
  100859986: "Rocket",
  100859990: "Rapid Strike",
  100860010: "Energy Blade",
  100925443: "Queen's Inspiration",
  100925444: "Bloodfeast",
  100925445: "Symbiotic Tail",
  100925447: "Destroy Ray",
  100925448: "Source Energy Guard",
  100925449: "Energy Overload",
  100990980: "Fire Path",
  101384194: "Destructive Lightning",
  101384195: "Thor",
  101384197: "Empress Escort",
  101449731: "Tactical Missile",
  101449732: "Mechanical Golem",
  101449736: "EMP Disruptor Ammo",
  101646338: "Kinetic Machine Gun",
  50397364: "Blaster Cannon",
  50397431: "Rapid Strike",
  50397503: "Rapid Strike Plus",
  50462842: "Air Support",
  50462843: "Rocket",
  50462844: "EMP Bomb",
});

const SOURCE_ID_GROUPS = Object.freeze({
  100859982: "skill",
  100859984: "skill",
  100859986: "skill",
  100859990: "skill",
  100860010: "skill",
  100925443: "titan",
  100925444: "titan",
  100925445: "titan",
  100925447: "titan",
  100925448: "titan",
  100925449: "titan",
  100990980: "titan",
  101384194: "titan",
  101384195: "titan",
  101384197: "titan",
  101449731: "warplane",
  101449732: "warplane",
  101449736: "warplane",
  101646338: "warplane",
  50397364: "spell",
  50397431: "skill",
  50397503: "skill",
  50462842: "skill",
  50462843: "skill",
  50462844: "skill",
});

const SOURCE_ID_TROOP_ALIASES = Object.freeze({
  100925442: new Set(["femaletitan"]),
  100925443: new Set(["femaletitan"]),
  100925444: new Set(["femaletitan"]),
  100925445: new Set(["femaletitan"]),
  100990978: new Set(["femaletitan"]),
  100990979: new Set(["femaletitan"]),
  101384197: new Set(["femaletitan"]),
  101384198: new Set(["femaletitan"]),
  101384199: new Set(["femaletitan"]),
  100925446: new Set(["maletitan"]),
  100925447: new Set(["maletitan"]),
  100925448: new Set(["maletitan"]),
  100925449: new Set(["maletitan"]),
  100990980: new Set(["maletitan"]),
  100990981: new Set(["maletitan"]),
  101384194: new Set(["maletitan"]),
  101384195: new Set(["maletitan"]),
  101384196: new Set(["maletitan"]),
  101449730: new Set(["warplane"]),
  101449731: new Set(["warplane"]),
  101449732: new Set(["warplane"]),
  101449734: new Set(["warplane"]),
  101449735: new Set(["warplane"]),
  101449736: new Set(["warplane"]),
  101449737: new Set(["warplane"]),
  101449738: new Set(["warplane"]),
  101646338: new Set(["warplane"]),
});

const humanizeToken = (raw) => String(raw || "")
  .trim()
  .split(/[_\s]+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
  .join(" ");

const normalizeTitanTerminology = (label) => String(label || "")
  .replace(/\bTitan Bison\b/gi, "Source Spirit")
  .replace(/\bBison\b/gi, "Source Spirit")
  .replace(/\bFemale Titan\b/gi, "Empress")
  .replace(/\bMale Titan\b/gi, "Source Spirit");

const isGenericSourceName = (label) => {
  const normalized = String(label || "").trim();
  return /^(?:Active Skill|Update Skill|Breakthrough Skill|Armor Skill|Misc Spell|Battle Spell|Titan Spell|Warplane Spell|Skill|Spell)\s+\d+$/i
    .test(normalized)
    || /^Warplane Kinetic Cannon Equip$/i.test(normalized);
};

const resolveSourceTypeLabel = (sourceType, group = "") => {
  if (group === "titan") return "Titan Effect";
  if (group === "warplane") return "Warplane Effect";
  if (group === "skill") return "Commander Skill";
  return SOURCE_TYPE_LABELS[String(sourceType || "").trim()] || humanizeToken(sourceType) || "Unknown Source";
};

const normalizeCatalogEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") return { label: entry, description: "" };
  return {
    label: String(entry?.label || entry?.name || "").trim(),
    description: String(entry?.description || "").trim(),
  };
};

const resolveSourceLabel = (source = {}, skillCatalogById = {}) => {
  const sourceName = String(source?.sourceName || "").trim();
  const sourceId = Math.trunc(asNumber(source?.sourceId, NaN));
  if (Number.isFinite(sourceId) && sourceId > 0) {
    const rawCatalogEntry = skillCatalogById?.[String(sourceId)] || skillCatalogById?.[sourceId];
    const catalogEntry = normalizeCatalogEntry(rawCatalogEntry);
    if (rawCatalogEntry?.sourceKind === "officer") {
      const officerLabel = String(rawCatalogEntry?.officerLabel || "").trim();
      const skillLabel = String(catalogEntry?.label || KNOWN_SOURCE_LABELS[sourceId] || "").trim();
      if (officerLabel && skillLabel) return normalizeTitanTerminology(`${officerLabel}: ${skillLabel}`);
      if (officerLabel) return normalizeTitanTerminology(officerLabel);
      if (skillLabel) return normalizeTitanTerminology(skillLabel);
    }
    if (sourceName && !isGenericSourceName(sourceName)) return normalizeTitanTerminology(sourceName);
    if (catalogEntry?.label) return normalizeTitanTerminology(catalogEntry.label);
    if (KNOWN_SOURCE_LABELS[sourceId]) return normalizeTitanTerminology(KNOWN_SOURCE_LABELS[sourceId]);
    return normalizeTitanTerminology(`${resolveSourceTypeLabel(source?.sourceType, SOURCE_ID_GROUPS[sourceId])} ${sourceId}`);
  }
  if (sourceName) return normalizeTitanTerminology(sourceName);
  return normalizeTitanTerminology(resolveSourceTypeLabel(source?.sourceType));
};

const resolveSourceGroup = (sourceType, sourceId = null) => {
  const normalizedId = Math.trunc(asNumber(sourceId, NaN));
  if (Number.isFinite(normalizedId) && SOURCE_ID_GROUPS[normalizedId]) {
    return SOURCE_ID_GROUPS[normalizedId];
  }
  const normalized = String(sourceType || "").trim();
  if (SOURCE_TYPE_GROUPS[normalized]) return SOURCE_TYPE_GROUPS[normalized];
  if (normalized.includes("titan")) return "titan";
  if (normalized.includes("warplane")) return "warplane";
  if (normalized.includes("skill")) return "skill";
  if (normalized.includes("spell")) return "spell";
  return "other";
};

const normalizeAttributionSources = (sourceAttribution, skillCatalogById = {}) => {
  const attribution = normalizeSourceAttribution(sourceAttribution);
  return attribution.sources
    .map((source, index) => {
      const sourceId = source?.sourceId ?? null;
      const group = resolveSourceGroup(source?.sourceType, sourceId);
      const catalogEntry = sourceId ? (skillCatalogById?.[String(sourceId)] || skillCatalogById?.[sourceId]) : null;
      return {
        key: `${String(source?.sourceType || "source")}:${String(sourceId ?? index)}`,
        sourceType: String(source?.sourceType || "unknown"),
        sourceId,
        label: resolveSourceLabel(source, skillCatalogById),
        typeLabel: catalogEntry?.sourceKind === "officer"
          ? "Officer Skill"
          : resolveSourceTypeLabel(source?.sourceType, group),
        showSourceId: !(catalogEntry?.label || catalogEntry?.sourceKind === "officer" || KNOWN_SOURCE_LABELS[Math.trunc(asNumber(sourceId, NaN))]),
        group,
        unitsKilled: Math.max(0, Math.floor(asNumber(source?.unitsKilled, 0))),
      };
    })
    .filter((source) => source.unitsKilled > 0)
    .sort((left, right) => (
      right.unitsKilled - left.unitsKilled
      || String(left.label).localeCompare(String(right.label))
    ));
};

const normalizeTroopAlias = (row = {}) => {
  const rawParts = [
    row?.troopId,
    row?.familyId,
    row?.troopGid,
    row?.troopName,
  ].map((value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""))
    .filter(Boolean);
  const joined = rawParts.join("|");
  if (joined.includes("female") && joined.includes("titan")) return "femaletitan";
  if ((joined.includes("male") || joined.includes("bison")) && joined.includes("titan")) return "maletitan";
  if (joined.includes("warplane")) return "warplane";
  if (joined.includes("zombie") || joined.includes("biochemical")) return "zombie";
  return rawParts[0] || "";
};

const buildReportRowKey = (row = {}, index = 0) => [
  row?.troopGid ?? "na",
  row?.troopId ?? row?.familyId ?? "troop",
  row?.troopName ?? "",
  index,
].map((part) => String(part)).join(":");

const sourceMatchesReportRow = (source = {}, row = {}, rowAlias = "", rowContext = {}) => {
  const sourceId = Math.trunc(asNumber(source?.sourceId, NaN));
  const rowTroopGid = Math.trunc(asNumber(row?.troopGid, NaN));
  if (source.group === "basic" && Number.isFinite(sourceId) && Number.isFinite(rowTroopGid)) {
    return sourceId === rowTroopGid;
  }
  if (Number.isFinite(sourceId) && SOURCE_ID_TROOP_ALIASES[sourceId]) {
    return SOURCE_ID_TROOP_ALIASES[sourceId].has(rowAlias);
  }
  if (source.group === "warplane" && rowAlias === "warplane") return true;
  if (source.group === "titan" && rowContext.titanRowCount === 1 && (rowAlias === "femaletitan" || rowAlias === "maletitan")) {
    return true;
  }
  return false;
};

const buildTroopRowAttribution = (rows = [], sourceAttribution = null, skillCatalogById = {}) => {
  const attributionRows = normalizeAttributionSources(sourceAttribution, skillCatalogById);
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const aliases = normalizedRows.map((row) => normalizeTroopAlias(row));
  const rowContext = {
    titanRowCount: aliases.filter((alias) => alias === "femaletitan" || alias === "maletitan").length,
  };
  const usedKeys = new Set();
  const byRowKey = {};
  normalizedRows.forEach((row, index) => {
    const rowAlias = aliases[index];
    const rowKey = buildReportRowKey(row, index);
    const matches = attributionRows.filter((source) => sourceMatchesReportRow(source, row, rowAlias, rowContext));
    if (matches.length) {
      matches.forEach((source) => usedKeys.add(source.key));
      byRowKey[rowKey] = matches;
    }
  });
  const unmapped = attributionRows.filter((source) => !usedKeys.has(source.key));
  return { byRowKey, unmapped };
};

const getTroopReportSortRank = (row = {}) => {
  const alias = normalizeTroopAlias(row);
  if (alias === "warplane") return 0;
  if (alias === "femaletitan" || alias === "maletitan") return 1;
  return 2;
};

const sortTroopReportRows = (rows = []) => (Array.isArray(rows) ? rows : [])
  .map((row, index) => ({ row, index, rank: getTroopReportSortRank(row) }))
  .sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    return a.index - b.index;
  })
  .map((entry) => entry.row);

const isVisibleTechnologyBonusRow = (row = {}) => {
  const label = String(row?.label || "").trim().toLowerCase();
  const key = String(row?.key || "").trim().toLowerCase();
  const hiddenLabels = new Set([
    "captured commander level",
    "captured battle ratio",
  ]);
  const hiddenKeys = new Set([
    "capturedcommanderlevel",
    "capturedbattleratio",
    "commanderlevel",
    "battleratio",
    "battle_ratio",
    "t_b_r",
  ]);
  return !hiddenLabels.has(label) && !hiddenKeys.has(key);
};

const resolveCommanderIdentity = (profile, fallbackLabel) => {
  const baseName = String(
    profile?.name
    || profile?.linkedCommanderName
    || fallbackLabel,
  ).trim() || fallbackLabel;

  const linkedName = String(profile?.linkedCommanderName || "").trim();
  const tag = String(
    profile?.linkedCommanderTag
    || profile?.allianceTag
    || profile?.tag
    || profile?.linkedCommander?.allianceTag
    || profile?.linkedCommander?.tag
    || "",
  ).trim();
  const nation = String(
    profile?.linkedCommanderNation
    || profile?.nation
    || profile?.linkedCommander?.nation
    || "",
  ).trim();
  const coords = profile?.linkedCommanderCoords && typeof profile.linkedCommanderCoords === "object"
    ? profile.linkedCommanderCoords
    : (profile?.coords && typeof profile.coords === "object" ? profile.coords : null);
  const coordX = Number(coords?.x);
  const coordY = Number(coords?.y);
  const hasCoords = Number.isFinite(coordX) && Number.isFinite(coordY);

  const detailParts = [];
  if (tag) detailParts.push(`[${tag}]`);
  if (nation) detailParts.push(`#${nation}`);
  if (hasCoords) detailParts.push(`X:${Math.floor(coordX)} Y:${Math.floor(coordY)}`);
  if (linkedName && linkedName !== baseName) detailParts.push(linkedName);

  return {
    name: baseName,
    detail: detailParts.join(" "),
  };
};

const SummaryCard = ({ title, sideClass, summary = {}, profile, fallbackName }) => {
  const identity = resolveCommanderIdentity(profile, fallbackName);
  return (
    <div className={`battle-report-summary-card ${sideClass}`}>
      <div className="battle-report-summary-head">
        <div className="battle-report-summary-title">{title}</div>
        <div className="battle-report-summary-name">{identity.name}</div>
        {identity.detail ? <div className="battle-report-summary-detail">{identity.detail}</div> : null}
      </div>
      <div className="battle-report-summary-grid">
        <div className="battle-report-metric"><span>Battlers</span><strong>{formatUnits(summary?.battlers)}</strong></div>
        <div className="battle-report-metric"><span>Total Units</span><strong>{formatUnits(summary?.totalUnits ?? summary?.totalTroops)}</strong></div>
        <div className="battle-report-metric"><span>Enemy Units Killed</span><strong>{formatOptionalUnits(summary?.enemyUnitsKilled ?? summary?.enemyTroopsKilled)}</strong></div>
        <div className="battle-report-metric"><span>Lost Units</span><strong>{formatOptionalUnits(summary?.lostUnits ?? summary?.lostTroops)}</strong></div>
        <div className="battle-report-metric"><span>Wounded Units</span><strong>{formatWounded(summary?.woundedUnits ?? summary?.woundedTroops)}</strong></div>
        <div className="battle-report-metric"><span>Surviving Units</span><strong>{formatOptionalUnits(summary?.survivingUnits ?? summary?.survivingTroops)}</strong></div>
      </div>
    </div>
  );
};

const SourceRow = ({ row }) => (
  <div className={`battle-report-source-row ${row.group}`}>
    <span className="battle-report-source-name">{row.label}</span>
    <strong>{formatOptionalUnits(row.unitsKilled)}</strong>
  </div>
);

const SourceBreakdownList = ({ rows = [] }) => {
  if (!rows.length) return null;
  const total = rows.reduce((sum, row) => sum + asNumber(row?.unitsKilled, 0), 0);
  return (
    <div className="battle-report-row-source-breakdown">
      <div className="battle-report-row-source-summary">
        <span>Attributed Kill Breakdown</span>
        <strong>{formatOptionalUnits(total)}</strong>
      </div>
      <div className="battle-report-source-list">
        {rows.map((row) => <SourceRow row={row} key={row.key} />)}
      </div>
    </div>
  );
};

const TroopTable = ({
  title,
  sideClass,
  rows = [],
  sourceAttribution = null,
  skillCatalogById = {},
}) => {
  const [expandedRows, setExpandedRows] = React.useState(() => new Set());
  const reportRows = React.useMemo(() => sortTroopReportRows(rows), [rows]);
  const { byRowKey, unmapped } = React.useMemo(
    () => buildTroopRowAttribution(reportRows, sourceAttribution, skillCatalogById),
    [reportRows, sourceAttribution, skillCatalogById],
  );
  const unmappedTotal = unmapped.reduce((sum, row) => sum + asNumber(row?.unitsKilled, 0), 0);
  const toggleRow = (rowKey) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  return (
    <div className={`battle-report-table-card ${sideClass}`}>
      <div className="battle-report-table-title">{title}</div>
      <div className="battle-report-table-wrap">
        <table className="battle-report-table">
          <thead>
            <tr>
              <th>Troop</th>
              <th>Tier / Level</th>
              <th>Starting Units</th>
              <th>Surviving Units</th>
              <th>Wounded Units</th>
              <th>Lost Units</th>
              <th>Enemy Units Killed</th>
            </tr>
          </thead>
          <tbody>
            {reportRows.map((row, idx) => {
              const rowKey = buildReportRowKey(row, idx);
              const breakdownRows = byRowKey[rowKey] || [];
              const isExpanded = expandedRows.has(rowKey);
              return (
                <React.Fragment key={rowKey}>
                  <tr className={breakdownRows.length ? "has-source-breakdown" : ""}>
                    <td className="battle-report-troop-cell">
                      {breakdownRows.length ? (
                        <button
                          type="button"
                          className={`battle-report-breakdown-toggle ${isExpanded ? "expanded" : ""}`}
                          onClick={() => toggleRow(rowKey)}
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? "Hide" : "Show"} kill breakdown for ${String(row?.troopName || "Unknown")}`}
                        >
                          {isExpanded ? "v" : ">"}
                        </button>
                      ) : <span className="battle-report-breakdown-spacer" />}
                      {row?.icon ? <img src={row.icon} alt={row?.troopName || "Troop"} /> : <span className="battle-report-troop-icon-fallback" />}
                      <span>{String(row?.troopName || "Unknown")}</span>
                    </td>
                    <td>{formatTierLabel(row)}</td>
                    <td>{formatOptionalUnits(row?.initialUnits ?? row?.startingUnits ?? row?.totalUnits ?? row?.initialTroops ?? row?.startingTroops ?? row?.totalTroops)}</td>
                    <td>{formatOptionalUnits(row?.survivingUnits ?? row?.survivingTroops)}</td>
                    <td>{formatWounded(row?.woundedUnits ?? row?.woundedTroops)}</td>
                    <td>{formatOptionalUnits(row?.lostUnits ?? row?.lostTroops)}</td>
                    <td>{formatOptionalUnits(row?.enemyUnitsKilled ?? row?.enemyTroopsKilled)}</td>
                  </tr>
                  {isExpanded ? (
                    <tr className="battle-report-breakdown-row">
                      <td colSpan={7}>
                        <SourceBreakdownList rows={breakdownRows} />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
            {unmapped.length ? (
              <React.Fragment>
                <tr className="has-source-breakdown battle-report-unmapped-source-row">
                  <td className="battle-report-troop-cell">
                    <button
                      type="button"
                      className={`battle-report-breakdown-toggle ${expandedRows.has("__unmapped") ? "expanded" : ""}`}
                      onClick={() => toggleRow("__unmapped")}
                      aria-expanded={expandedRows.has("__unmapped")}
                      aria-label="Show other attributed kill effects"
                    >
                      {expandedRows.has("__unmapped") ? "v" : ">"}
                    </button>
                    <span className="battle-report-troop-icon-fallback" />
                    <span>Other Attributed Effects</span>
                  </td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>{formatOptionalUnits(unmappedTotal)}</td>
                </tr>
                {expandedRows.has("__unmapped") ? (
                  <tr className="battle-report-breakdown-row">
                    <td colSpan={7}>
                      <SourceBreakdownList rows={unmapped} />
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CommanderBonusPanel = ({ title, sideClass, rows = [], officerEffects = [] }) => {
  const visibleRows = (Array.isArray(rows) ? rows : []).filter(isVisibleTechnologyBonusRow);
  const visibleOfficerEffects = (Array.isArray(officerEffects) ? officerEffects : []).slice(0, 14);
  return (
    <div className={`battle-report-bonus-card ${sideClass}`}>
      <div className="battle-report-table-title">{title}</div>
      <div className="battle-report-bonus-grid">
        {visibleRows.map((row, idx) => (
          <div className="battle-report-bonus-row" key={`${row?.key || "metric"}-${idx}`}>
            <span>{String(row?.label || row?.key || "Metric")}</span>
            <strong>{formatBonusValue(row)}</strong>
          </div>
        ))}
        {visibleOfficerEffects.map((row, idx) => (
          <div className="battle-report-bonus-row battle-report-bonus-row--officer" key={`officer-${row?.effectId || "metric"}-${idx}`}>
            <span>{`${String(row?.officerName || "Officer")} - ${String(row?.skillName || row?.source || "Skill")}`}</span>
            <strong>{String(row?.displayValue || row?.value || "0")}</strong>
          </div>
        ))}
        {visibleRows.length === 0 && visibleOfficerEffects.length === 0 ? (
          <div className="battle-report-bonus-empty">No bonus metrics were resolved from selected commander inputs.</div>
        ) : null}
      </div>
    </div>
  );
};

const TroopReportTables = ({
  fallbackTitle,
  sideClass,
  rows = [],
  groups = [],
  sourceAttribution = null,
  skillCatalogById = {},
}) => {
  const normalizedGroups = Array.isArray(groups) ? groups.filter((group) => Array.isArray(group?.rows) && group.rows.length > 0) : [];
  if (normalizedGroups.length) {
    return normalizedGroups.map((group, index) => (
      <TroopTable
        key={`${sideClass}-group-${index}`}
        title={group?.title || fallbackTitle}
        sideClass={sideClass}
        rows={group.rows}
        sourceAttribution={group?.sourceAttribution || null}
        skillCatalogById={skillCatalogById}
      />
    ));
  }
  return (
    <TroopTable
      title={fallbackTitle}
      sideClass={sideClass}
      rows={rows}
      sourceAttribution={sourceAttribution}
      skillCatalogById={skillCatalogById}
    />
  );
};

const ReportBlock = ({
  title,
  battleReport = null,
  attackerProfile = null,
  defenderProfile = null,
  skillCatalogById = {},
  showBonuses = true,
}) => {
  if (!battleReport || typeof battleReport !== "object") return null;
  return (
    <section className="battle-report-block" aria-label={title}>
      <div className="battle-report-block-title">{title}</div>
      <div className="battle-report-grid">
        <SummaryCard
          title="Attacker"
          sideClass="attacker"
          summary={battleReport?.attackerSummary || {}}
          profile={attackerProfile}
          fallbackName="Attacker Commander"
        />
        <SummaryCard
          title="Defender"
          sideClass="defender"
          summary={battleReport?.defenderSummary || {}}
          profile={defenderProfile}
          fallbackName="Defender Commander"
        />
        <div className="battle-report-side-column attacker battle-report-troop-column">
          <TroopReportTables
            fallbackTitle="Attacker Troop Report"
            sideClass="attacker"
            rows={battleReport?.attackerTroops || []}
            groups={battleReport?.attackerTroopGroups || []}
            sourceAttribution={battleReport?.attackerSourceAttribution}
            skillCatalogById={skillCatalogById}
          />
        </div>
        <div className="battle-report-side-column defender battle-report-troop-column">
          <TroopReportTables
            fallbackTitle="Defender Troop Report"
            sideClass="defender"
            rows={battleReport?.defenderTroops || []}
            groups={battleReport?.defenderTroopGroups || []}
            sourceAttribution={battleReport?.defenderSourceAttribution}
            skillCatalogById={skillCatalogById}
          />
        </div>
        {showBonuses ? (
          <div className="battle-report-side-column attacker battle-report-bonus-column">
            <CommanderBonusPanel
              title="Attacker Technology Bonuses"
              sideClass="attacker"
              rows={battleReport?.attackerBonuses || []}
              officerEffects={battleReport?.attackerOfficerEffects || []}
            />
          </div>
        ) : null}
        {showBonuses ? (
          <div className="battle-report-side-column defender battle-report-bonus-column">
            <CommanderBonusPanel
              title="Defender Technology Bonuses"
              sideClass="defender"
              rows={battleReport?.defenderBonuses || []}
              officerEffects={battleReport?.defenderOfficerEffects || []}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

const CompareDeltaPanel = ({ compareDelta = null }) => {
  if (!compareDelta || typeof compareDelta !== "object") return null;
  const attackerSummary = compareDelta?.battleReport?.attacker?.summary || {};
  const defenderSummary = compareDelta?.battleReport?.defender?.summary || {};
  return (
    <section className="battle-report-block" aria-label="Baseline vs Variant Delta">
      <div className="battle-report-block-title">Baseline vs Variant Delta</div>
      <div className="battle-report-grid">
        <div className="battle-report-bonus-card attacker">
          <div className="battle-report-table-title">Attacker Delta</div>
          <div className="battle-report-bonus-grid">
            <div className="battle-report-bonus-row">
              <span>Winner Changed</span>
              <strong>{compareDelta?.winnerChanged ? "Yes" : "No"}</strong>
            </div>
            <div className="battle-report-bonus-row">
              <span>Lost Units Delta</span>
              <strong>{formatOptionalUnits(attackerSummary?.lostUnitsDelta)}</strong>
            </div>
            <div className="battle-report-bonus-row">
              <span>Enemy Units Killed Delta</span>
              <strong>{formatOptionalUnits(attackerSummary?.enemyUnitsKilledDelta)}</strong>
            </div>
            <div className="battle-report-bonus-row">
              <span>Surviving Units Delta</span>
              <strong>{formatOptionalUnits(attackerSummary?.survivingUnitsDelta)}</strong>
            </div>
          </div>
        </div>
        <div className="battle-report-bonus-card defender">
          <div className="battle-report-table-title">Defender Delta</div>
          <div className="battle-report-bonus-grid">
            <div className="battle-report-bonus-row">
              <span>Winner Baseline → Variant</span>
              <strong>{`${String(compareDelta?.winnerBaseline || "n/a")} -> ${String(compareDelta?.winnerVariant || "n/a")}`}</strong>
            </div>
            <div className="battle-report-bonus-row">
              <span>Lost Units Delta</span>
              <strong>{formatOptionalUnits(defenderSummary?.lostUnitsDelta)}</strong>
            </div>
            <div className="battle-report-bonus-row">
              <span>Enemy Units Killed Delta</span>
              <strong>{formatOptionalUnits(defenderSummary?.enemyUnitsKilledDelta)}</strong>
            </div>
            <div className="battle-report-bonus-row">
              <span>Surviving Units Delta</span>
              <strong>{formatOptionalUnits(defenderSummary?.survivingUnitsDelta)}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ReportDiagnostics = ({ diagnostics = null }) => {
  if (!diagnostics || typeof diagnostics !== "object") return null;
  const runtimeSource = String(diagnostics?.runtimeSource || "").trim();
  const reportBaselineId = String(diagnostics?.reportBaselineId || "").trim();
  const reportId = String(diagnostics?.reportId || "").trim();
  if (!runtimeSource && !reportBaselineId && !reportId) return null;
  const parts = [];
  if (runtimeSource) parts.push(`runtime: ${runtimeSource}`);
  if (reportBaselineId) parts.push(`reportBaselineId: ${reportBaselineId}`);
  if (reportId) parts.push(`reportId: ${reportId}`);
  return (
    <div className="battle-report-empty">
      {parts.join(" | ")}
    </div>
  );
};

const BattleReportSection = ({
  battleReport = null,
  compareDelta = null,
  attackerProfile = null,
  defenderProfile = null,
  reportDiagnostics = null,
  skillCatalogById = {},
  title = "",
  emptyMessage = "Run Server Parity to generate battle report.",
  showBonuses = true,
}) => {
  const resolvedReport = battleReport && typeof battleReport === "object" ? battleReport : null;

  if (!resolvedReport) {
    return (
      <div className="battle-report-empty">
        {emptyMessage}
      </div>
    );
  }

  const reportTitle = title || (compareDelta ? "Variant Result" : "Simulation Result");
  return (
    <section className="battle-report-section" aria-label="Battle Report">
      <ReportDiagnostics diagnostics={reportDiagnostics} />
      <ReportBlock
        title={reportTitle}
        battleReport={resolvedReport}
        attackerProfile={attackerProfile}
        defenderProfile={defenderProfile}
        skillCatalogById={skillCatalogById}
        showBonuses={showBonuses}
      />
      <CompareDeltaPanel compareDelta={compareDelta} />
    </section>
  );
};

export default BattleReportSection;
